import { useMemo } from 'react';
import { Box } from '@mui/material';

interface AudioWaveformProps {
  durationMs?: number | null;
  isPlaying?: boolean;
  width?: number | string;
  height?: number;
}

/**
 * Simple pseudo-waveform visualization using SVG bars.
 * Generates deterministic bars from duration data.
 */
export function AudioWaveform({
  durationMs,
  isPlaying = false,
  width = '100%',
  height = 32,
}: AudioWaveformProps) {
  const bars = useMemo(() => {
    const count = 40;
    const result: number[] = [];
    // Use a simple deterministic pseudo-random from the duration.
    const seed = durationMs ?? 1000;
    for (let i = 0; i < count; i++) {
      const x = Math.sin(seed * 0.001 + i * 0.7) * 0.5 + 0.5;
      result.push(0.15 + x * 0.85);
    }
    return result;
  }, [durationMs]);

  const barWidth = 2;
  const gap = 1;
  const totalWidth = bars.length * (barWidth + gap);

  return (
    <Box
      sx={{
        width,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${totalWidth} ${height}`}
        preserveAspectRatio="none"
      >
        {bars.map((amplitude, i) => {
          const barHeight = amplitude * height;
          const y = (height - barHeight) / 2;
          return (
            <rect
              key={i}
              x={i * (barWidth + gap)}
              y={y}
              width={barWidth}
              height={barHeight}
              rx={1}
              fill={isPlaying ? '#1976d2' : '#9e9e9e'}
              opacity={isPlaying ? 0.8 : 0.4}
            />
          );
        })}
      </svg>
    </Box>
  );
}
